import ImageUpload from '../components/ImageUpload';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme, useAppStyles } from '../theme';
import { useStore } from '../store';
import { useStatusBar } from './SharedComponents';
import { useLang, LANGUAGES, useT } from '../i18n';
import { wallet, disputes as disputesApi, reviews as reviewsApi, roles as rolesApi, roleDashboard, kyc, rolesConfig, myOrders as myOrdersApi, provider as providerApi } from '../services/api';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { getPushPermission, isPushSupported, subscribePush, unsubscribePush, testPush } from '../services/pushHelper';

const LEVELS = [
  { min:0,    label:'Starter',  color:'#9ca3af', icon:'🌱' },
  { min:100,  label:'Bronze',   color:'#cd7f32', icon:'🥉' },
  { min:500,  label:'Silver',   color:'#c0c0c0', icon:'🥈' },
  { min:1500, label:'Gold',     color:'#f5a623', icon:'🥇' },
  { min:5000, label:'Platinum', color:'#e5e7eb', icon:'💎' },
];
const getLevel = txc => [...LEVELS].reverse().find(l => txc >= l.min) || LEVELS[0];
const getNextLevel = txc => LEVELS.find(l => l.min > txc);

function StatCard({ label, value, color, sub, C }) {
  return (
    <div style={{ background: C ? C.input : 'rgba(255,255,255,0.08)', borderRadius:12, padding:'12px 8px', textAlign:'center' }}>
      <div style={{ fontSize:16, fontWeight:900, color: C ? C.text : '#fff' }}>{value ?? '—'}</div>
      <div style={{ fontSize:9, color: C ? C.muted : 'rgba(255,255,255,0.65)', fontWeight:600, marginTop:2 }}>{label}</div>
      {sub && <div style={{ fontSize:9, color: C ? C.muted : 'rgba(255,255,255,0.45)', marginTop:1 }}>{sub}</div>}
    </div>
  );
}

import { DynamicField, DynamicServiceForm, ProviderOnboardingWizard } from '../components/ProviderWizard';

function ProviderDashboard({ C, nav }) {
  const [ptab, setPtab] = React.useState('browse');
  const [categories, setCategories] = React.useState([]);
  const [myCategories, setMyCategories] = React.useState([]);
  const [allPlans, setAllPlans] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [requests, setRequests] = React.useState([]);
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showAddCat, setShowAddCat] = React.useState(false);
  const [showAddSvc, setShowAddSvc] = React.useState(false);
  const [editSvc, setEditSvc] = React.useState(null);
  const [svcForm, setSvcForm] = React.useState({category_id:'',name:'',description:'',price_txc:'',unit:'per service'});
  const [accepting, setAccepting] = React.useState(null);
  const [toast, setToast] = React.useState('');
  const [filters, setFilters] = React.useState({q:'',location:'',sort:'recent',min_budget:'',max_budget:''});
  const [showFilters, setShowFilters] = React.useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(''),3000); };

  React.useEffect(() => {
    Promise.all([
      providerApi.categories(),
      providerApi.myCategories(),
      providerApi.myServices(),
      providerApi.browseRequests(),
      providerApi.myOrders(),
    ]).then(([cats, myCats, svcs, reqs, ords]) => {
      setAllPlans(cats.plans||[]);
      setMyCategories(myCats.categories||[]);
      setServices(svcs.services||[]);
      setRequests(reqs.requests||[]);
      setOrders(ords.orders||[]);
    }).finally(()=>setLoading(false));
  }, []);

  const handleSubscribe = async (plan) => {
    try {
      await providerApi.subscribe({category_id: plan.id});
      showToast(`✓ Subscribed to ${plan.name}!`);
      const r = await providerApi.myCategories();
      setMyCategories(r.categories||[]);
      setShowAddCat(false);
      // Launch onboarding wizard
      setOnboardingCat(plan);
    } catch(e) { showToast('❌ '+e.message); }
  };

  const handleAddService = async () => {
    if (!svcForm.category_id || !svcForm.name || !svcForm.price_txc) {
      showToast('Fill all required fields'); return;
    }
    try {
      if (editSvc) {
        await providerApi.updateService(editSvc.id, {...svcForm, price_txc: parseFloat(svcForm.price_txc), is_active: svcForm.is_active !== false});
        showToast('✓ Service updated!');
      } else {
        await providerApi.addService({...svcForm, price_txc: parseFloat(svcForm.price_txc)});
        showToast('✓ Service added!');
      }
      const r = await providerApi.myServices();
      setServices(r.services||[]);
      setShowAddSvc(false); setEditSvc(null);
      setSvcForm({category_id:'',name:'',description:'',price_txc:'',unit:'per service'});
    } catch(e) { showToast('❌ '+e.message); }
  };

  const handleDeleteService = async (id) => {
    try {
      await providerApi.deleteService(id);
      showToast('✓ Service deleted');
      setServices(prev => prev.filter(s=>s.id!==id));
    } catch(e) { showToast('❌ '+e.message); }
  };

  const handleAccept = async (reqId) => {
    setAccepting(reqId);
    try {
      await providerApi.acceptRequest({request_id: reqId});
      showToast('✓ Request accepted!');
      setRequests(prev => prev.filter(r=>r.id!==reqId));
      const r = await providerApi.myOrders();
      setOrders(r.orders||[]);
      setPtab('orders');
    } catch(e) { showToast('❌ '+e.message); }
    setAccepting(null);
  };

  const statusColor = (s) => ({
    open:'#10b981', accepted:'#1a3cc8', in_progress:'#f97316',
    completed:'#7c3aed', cancelled:'#ef4444',
  }[s]||'#9ca3af');

  if (loading) return (
    <div style={{textAlign:'center',padding:40}}>
      <div style={{width:32,height:32,border:`3px solid ${C.border}`,borderTop:`3px solid #0891b2`,borderRadius:'50%',margin:'0 auto',animation:'spin 0.8s linear infinite'}}/>
    </div>
  );

  return (
    <div>
      {onboardingCat && (
        <ProviderOnboardingWizard
          category={onboardingCat}
          C={C}
          nav={nav}
          onComplete={()=>{ setOnboardingCat(null); loadData(); }}
          onSkip={()=>setOnboardingCat(null)}
        />
      )}
      {toast && (
        <div style={{position:'fixed',top:80,left:'50%',transform:'translateX(-50%)',background:'#1a1a2e',color:'#fff',padding:'10px 20px',borderRadius:20,zIndex:99999,fontSize:13,fontWeight:600,whiteSpace:'nowrap'}}>
          {toast}
        </div>
      )}

      {/* Tab bar */}
      <div style={{display:'flex',background:C.input,borderRadius:14,padding:3,marginBottom:16,gap:2}}>
        {[
          {id:'browse',   label:'🔍 Requests', count: requests.length},
          {id:'orders',   label:'📦 My Orders', count: orders.length},
          {id:'services', label:'🛠 My Services', count: services.length},
        ].map(t=>(
          <button key={t.id} onClick={()=>setPtab(t.id)} style={{
            flex:1,padding:'9px 4px',borderRadius:11,border:'none',cursor:'pointer',
            background:ptab===t.id?'#0891b2':'transparent',
            color:ptab===t.id?'#fff':C.muted,
            fontSize:11,fontWeight:ptab===t.id?700:400,
            position:'relative',
          }}>
            {t.label}
            {t.count>0 && <span style={{
              position:'absolute',top:4,right:4,
              background:ptab===t.id?'rgba(255,255,255,0.3)':'#ef4444',
              color:'#fff',borderRadius:10,fontSize:9,fontWeight:700,
              padding:'1px 5px',minWidth:16,textAlign:'center',
            }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* BROWSE REQUESTS */}
      {ptab==='browse' && (
        <div>
          {/* Expiring soon banner */}
          <ExpiringBanner C={C} nav={nav} />
          {myCategories.length===0 ? (
            <div style={{textAlign:'center',padding:'30px 20px',background:C.card,borderRadius:18,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:40,marginBottom:10}}>📋</div>
              <div style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:6}}>No Categories Yet</div>
              <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Subscribe to categories to see matching requests</div>
              <button onClick={()=>setShowAddCat(true)} style={{background:'#0891b2',border:'none',borderRadius:12,padding:'11px 24px',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer'}}>
                + Add Category
              </button>
            </div>
          ) : requests.length===0 ? (
            <div>
              <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:'12px 16px',marginBottom:16}}>
                <div style={{fontSize:12,color:C.muted,marginBottom:8,fontWeight:600}}>Your Categories</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {myCategories.map((cat,i)=>(
                    <span key={i} style={{background:'#0891b218',color:'#0891b2',padding:'4px 10px',borderRadius:20,fontSize:11,fontWeight:600}}>
                      {cat.icon} {cat.name}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{textAlign:'center',padding:'24px 20px',background:C.card,borderRadius:18,border:`1px solid ${C.border}`}}>
                <div style={{fontSize:32,marginBottom:8}}>🔍</div>
                <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:4}}>No Open Requests</div>
                <div style={{fontSize:12,color:C.muted}}>New requests matching your categories will appear here</div>
              </div>
            </div>
          ) : (
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                <div style={{fontSize:12,color:C.muted,fontWeight:600}}>{requests.length} open requests</div>
                <button onClick={()=>setShowAddCat(true)} style={{background:'#0891b218',border:'1px solid #0891b233',borderRadius:10,padding:'5px 12px',color:'#0891b2',fontSize:11,fontWeight:600,cursor:'pointer'}}>
                  + Category
                </button>
              </div>
              {requests.map((req,i)=>(
                <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:'14px 16px',marginBottom:10,boxShadow:C.shadow}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                    <div style={{flex:1,paddingRight:8}}>
                      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                        <span style={{fontSize:16}}>{req.cat_icon}</span>
                        <span style={{fontSize:13,fontWeight:700,color:C.text}}>{req.title}</span>
                      </div>
                      <div style={{fontSize:11,color:C.muted,marginBottom:4}}>{req.cat_name} · {req.location||'Location not specified'}</div>
                      {req.description && <div style={{fontSize:11,color:C.muted,lineHeight:1.4}}>{req.description.slice(0,100)}{req.description.length>100?'...':''}</div>}
                    </div>
                    <div style={{textAlign:'right',flexShrink:0}}>
                      {req.budget>0 && <div style={{fontSize:14,fontWeight:800,color:'#10b981'}}>{req.budget} TXC</div>}
                      <div style={{fontSize:10,color:C.muted}}>{req.quote_count} quotes</div>
                    </div>
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div style={{fontSize:10,color:C.muted}}>{new Date(req.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</div>
                    <button onClick={()=>handleAccept(req.id)} disabled={accepting===req.id} style={{
                      background:accepting===req.id?C.muted:'#10b981',border:'none',
                      borderRadius:10,padding:'8px 20px',color:'#fff',
                      fontSize:12,fontWeight:700,cursor:'pointer',
                    }}>
                      {accepting===req.id?'Accepting...':'✓ Accept'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* MY ORDERS */}
      {ptab==='orders' && (
        <div>
          {orders.length===0 ? (
            <div style={{textAlign:'center',padding:'30px 20px',background:C.card,borderRadius:18,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:40,marginBottom:10}}>📦</div>
              <div style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:6}}>No Orders Yet</div>
              <div style={{fontSize:12,color:C.muted}}>Accepted requests will appear here</div>
            </div>
          ) : orders.map((o,i)=>(
            <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:'14px 16px',marginBottom:10,boxShadow:C.shadow}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:3}}>{o.title}</div>
                  <div style={{fontSize:11,color:C.muted}}>{o.category} · {o.buyer_name||'Buyer'}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  {o.amount>0 && <div style={{fontSize:14,fontWeight:800,color:'#10b981'}}>{Math.round(o.amount).toLocaleString()} TXC</div>}
                </div>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{background:statusColor(o.status)+'18',color:statusColor(o.status),padding:'3px 10px',borderRadius:8,fontSize:10,fontWeight:700}}>
                  {o.status.toUpperCase()}
                </span>
                <div style={{display:'flex',gap:6}}>
                  <button onClick={()=>nav(`/chat/order/${o.id}`)} style={{background:'#7c3aed18',border:'1px solid #7c3aed33',borderRadius:8,padding:'5px 10px',color:'#7c3aed',fontSize:10,fontWeight:700,cursor:'pointer'}}>
                    💬 Chat
                  </button>
                  {o.status==='accepted' && (
                    <button onClick={async()=>{ await providerApi.updateOrder(o.id,{status:'in_progress'}); const r=await providerApi.myOrders(); setOrders(r.orders||[]); showToast('Status updated'); }} style={{background:'#f9731618',border:'1px solid #f9731633',borderRadius:8,padding:'5px 10px',color:'#f97316',fontSize:10,fontWeight:700,cursor:'pointer'}}>
                      Start Work
                    </button>
                  )}
                  {o.status==='in_progress' && (
                    <button onClick={async()=>{ await providerApi.updateOrder(o.id,{status:'completed'}); const r=await providerApi.myOrders(); setOrders(r.orders||[]); showToast('Marked complete!'); }} style={{background:'#10b98118',border:'1px solid #10b98133',borderRadius:8,padding:'5px 10px',color:'#10b981',fontSize:10,fontWeight:700,cursor:'pointer'}}>
                      Mark Done
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MY SERVICES */}
      {ptab==='services' && (
        <div>
          <div style={{display:'flex',gap:8,marginBottom:16}}>
            <button onClick={()=>setShowAddCat(true)} style={{flex:1,background:'#0891b218',border:'1px solid #0891b233',borderRadius:12,padding:'10px',color:'#0891b2',fontSize:12,fontWeight:700,cursor:'pointer'}}>
              + Subscribe Category
            </button>
            <button onClick={()=>{setShowAddSvc(true);setEditSvc(null);setSvcForm({category_id:myCategories[0]?.category_id||'',name:'',description:'',price_txc:'',unit:'per service'});}} style={{flex:1,background:'#10b98118',border:'1px solid #10b98133',borderRadius:12,padding:'10px',color:'#10b981',fontSize:12,fontWeight:700,cursor:'pointer'}}>
              + Add Service
            </button>
          </div>

          {/* My subscribed categories */}
          {myCategories.length>0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:8,textTransform:'uppercase'}}>Subscribed Categories</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {myCategories.map((cat,i)=>(
                  <div key={i} style={{background:'#0891b218',border:'1px solid #0891b233',borderRadius:20,padding:'5px 12px',display:'flex',alignItems:'center',gap:5}}>
                    <span>{cat.icon}</span>
                    <span style={{fontSize:11,fontWeight:600,color:'#0891b2'}}>{cat.name}</span>
                    <span style={{fontSize:9,color:C.muted}}>·{cat.service_count} services</span>
                    <span style={{fontSize:9,color:C.muted}}>expires {new Date(cat.expires_at).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {services.length===0 ? (
            <div style={{textAlign:'center',padding:'24px',background:C.card,borderRadius:18,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:32,marginBottom:8}}>🛠️</div>
              <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:4}}>No Services Yet</div>
              <div style={{fontSize:12,color:C.muted}}>Add services to attract customers</div>
            </div>
          ) : services.map((svc,i)=>(
            <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:'14px 16px',marginBottom:10,boxShadow:C.shadow}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:3}}>
                    <span style={{fontSize:16}}>{svc.category_icon}</span>
                    <span style={{fontSize:13,fontWeight:700,color:C.text}}>{svc.name}</span>
                    {!svc.is_active && <span style={{background:'#ef444418',color:'#ef4444',padding:'1px 6px',borderRadius:6,fontSize:9,fontWeight:700}}>INACTIVE</span>}
                  </div>
                  <div style={{fontSize:11,color:C.muted,marginBottom:4}}>{svc.category_name}</div>
                  {svc.description && <div style={{fontSize:11,color:C.muted}}>{svc.description}</div>}
                </div>
                <div style={{textAlign:'right',flexShrink:0}}>
                  <div style={{fontSize:14,fontWeight:800,color:'#10b981'}}>{svc.price_txc} TXC</div>
                  <div style={{fontSize:10,color:C.muted}}>{svc.unit}</div>
                </div>
              </div>
              <div style={{display:'flex',gap:6,marginTop:10}}>
                <button onClick={()=>{setEditSvc(svc);setSvcForm({category_id:svc.category_id,name:svc.name,description:svc.description||'',price_txc:svc.price_txc,unit:svc.unit||'per service',is_active:svc.is_active!==false,images:svc.images||[]});setShowAddSvc(true);}} style={{flex:1,background:C.input,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px',color:C.text,fontSize:11,fontWeight:600,cursor:'pointer'}}>
                  ✏️ Edit
                </button>
                <button onClick={()=>handleDeleteService(svc.id)} style={{flex:1,background:'#ef444412',border:'1px solid #ef444433',borderRadius:8,padding:'6px',color:'#ef4444',fontSize:11,fontWeight:600,cursor:'pointer'}}>
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SUBSCRIBE CATEGORY MODAL */}
      {showAddCat && (
        <div onClick={()=>setShowAddCat(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:10000,display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.bg,borderRadius:'24px 24px 0 0',width:'100%',maxWidth:430,maxHeight:'80vh',display:'flex',flexDirection:'column'}}>
            <div style={{padding:'16px 16px 12px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
              <div style={{fontSize:16,fontWeight:800,color:C.text}}>Subscribe to Category</div>
              <div style={{fontSize:11,color:C.muted}}>Pay once/month to receive matching requests</div>
            </div>
            <div style={{overflowY:'auto',flex:1,padding:16}}>
              {allPlans.filter(p=>!myCategories.find(c=>c.category_id===p.id)).map((plan,i)=>(
                <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:'12px 14px',marginBottom:10,display:'flex',alignItems:'center',gap:12}}>
                  <div style={{fontSize:28,flexShrink:0}}>{plan.icon}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{plan.name}</div>
                    <div style={{fontSize:11,color:C.muted}}>{plan.description}</div>
                  </div>
                  <button onClick={()=>setConfirmPlan(plan)} disabled={userBal < plan.price_txc} style={{background: userBal < plan.price_txc ? C.border : '#0891b2',border:'none',borderRadius:10,padding:'8px 14px',color:'#fff',fontSize:12,fontWeight:700,cursor:userBal < plan.price_txc ? 'not-allowed' : 'pointer',flexShrink:0,minWidth:85}}>
                    {userBal < plan.price_txc ? '🔒 Low bal' : `${plan.price_txc} TXC`}
                  </button>
                </div>
              ))}
              {allPlans.filter(p=>!myCategories.find(c=>c.category_id===p.id)).length===0 && (
                <div style={{textAlign:'center',padding:24,color:C.muted,fontSize:13}}>All categories subscribed!</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ADD/EDIT SERVICE MODAL */}
      {showAddSvc && (
        <div onClick={()=>setShowAddSvc(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:10000,display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.bg,borderRadius:'24px 24px 0 0',width:'100%',maxWidth:430,padding:'20px 16px 32px'}}>
            <div style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:16}}>{editSvc?'Edit Service':'Add New Service'}</div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:5}}>Category *</div>
              <select value={svcForm.category_id} onChange={e=>setSvcForm(p=>({...p,category_id:e.target.value}))}
                style={{width:'100%',padding:'11px 14px',background:C.input,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:13,boxSizing:'border-box'}}>
                <option value="">Select Category</option>
                {myCategories.map((cat,i)=>(
                  <option key={i} value={cat.category_id}>{cat.icon} {cat.name}</option>
                ))}
              </select>
            </div>
            {[
              {label:'Service Name *', key:'name', type:'text', placeholder:'e.g. Deep Home Cleaning'},
              {label:'Description',    key:'description', type:'text', placeholder:'What you offer...'},
              {label:'Price (TXC) *',  key:'price_txc', type:'number', placeholder:'e.g. 200'},
              {label:'Unit',           key:'unit', type:'text', placeholder:'e.g. per visit, per hour'},
            ].map((f,i)=>(
              <div key={i} style={{marginBottom:12}}>
                <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:5}}>{f.label}</div>
                <input type={f.type} value={svcForm[f.key]||''} placeholder={f.placeholder}
                  onChange={e=>setSvcForm(p=>({...p,[f.key]:e.target.value}))}
                  style={{width:'100%',padding:'11px 14px',background:C.input,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:13,boxSizing:'border-box',outline:'none'}}
                />
              </div>
            ))}
            <div style={{marginBottom:14}}>
              <ImageUpload C={C} value={svcForm.images || []} onChange={imgs=>setSvcForm(p=>({...p,images:imgs}))} max={5} label="Service Photos (optional)" />
            </div>
            {editSvc && (
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 14px',background:C.input,borderRadius:10,marginBottom:14}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:C.text}}>Service Active</div>
                  <div style={{fontSize:11,color:C.muted}}>Toggle off to pause this service</div>
                </div>
                <label style={{position:'relative',display:'inline-block',width:44,height:24,cursor:'pointer'}}>
                  <input type="checkbox" checked={svcForm.is_active !== false}
                    onChange={e=>setSvcForm(p=>({...p,is_active:e.target.checked}))}
                    style={{opacity:0,width:0,height:0}} />
                  <span style={{
                    position:'absolute',inset:0,
                    background:svcForm.is_active!==false?'#10b981':C.border,
                    borderRadius:24,transition:'.2s',
                  }}>
                    <span style={{
                      position:'absolute',height:18,width:18,left:svcForm.is_active!==false?23:3,
                      top:3,background:'#fff',borderRadius:'50%',transition:'.2s',
                    }} />
                  </span>
                </label>
              </div>
            )}
            <button onClick={handleAddService} style={{width:'100%',background:'#10b981',border:'none',borderRadius:14,padding:'14px',color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer'}}>
              {editSvc?'Update Service':'Add Service'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────
// ACTIVE BUSINESSES CARD (on Overview tab)
// ────────────────────────────────────────────────────────
function ActiveBusinessesCard({ C, onClickTab }) {
  const [cats, setCats] = React.useState([]);
  React.useEffect(() => {
    providerApi.myCategories()
      .then(r => setCats(r.categories||[]))
      .catch(()=>{});
  }, []);

  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:18,padding:20,marginBottom:16,boxShadow:C.shadow}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div style={{fontSize:13,fontWeight:700,color:C.text}}>Active Businesses</div>
        <span style={{background:C.primary+'18',color:C.primary,padding:'2px 10px',borderRadius:20,fontSize:11,fontWeight:700}}>
          {cats.length}
        </span>
      </div>
      {cats.length === 0 ? (
        <div style={{textAlign:'center',padding:'16px 12px'}}>
          <div style={{fontSize:32,marginBottom:6}}>🏪</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:10}}>No businesses yet</div>
          <button onClick={()=>nav('/explore-sections?tab=business')} style={{background:C.primary,border:'none',borderRadius:10,padding:'8px 20px',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer'}}>
            Start a Business →
          </button>
        </div>
      ) : (
        <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
          {cats.map((cat,i)=>(
            <div key={i} onClick={()=>nav('/explore-sections?tab=business')} style={{
              background:'#0891b218',border:'1px solid #0891b233',
              borderRadius:20,padding:'5px 12px',cursor:'pointer',
              display:'flex',alignItems:'center',gap:5,
            }}>
              <span>{cat.icon}</span>
              <span style={{fontSize:11,fontWeight:600,color:'#0891b2'}}>{cat.name}</span>
              {cat.service_count > 0 && (
                <span style={{fontSize:9,color:C.muted}}>·{cat.service_count}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────
// MY BUSINESS TAB - Category-based businesses
// ────────────────────────────────────────────────────────
function BusinessTab({ C, nav }) {
  const [onboardingCat, setOnboardingCat] = React.useState(null);
  const [cats, setCats] = React.useState([]);
  const [allPlans, setAllPlans] = React.useState([]);
  const [selectedCat, setSelectedCat] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [showAdd, setShowAdd] = React.useState(false);
  const [bizSearch, setBizSearch] = React.useState('');
  const [confirmPlan, setConfirmPlan] = React.useState(null);
  const [subscribing, setSubscribing] = React.useState(false);
  const [userBal, setUserBal] = React.useState(0);
  
  React.useEffect(() => {
    if (!showAdd) return;
    // Match WalletScreen's exact pattern
    import('../services/api').then(({ wallet }) => {
      const uid = JSON.parse(localStorage.getItem('vgo.user')||'{}').id;
      wallet.balance(uid).then(b => setUserBal(b?.txc_balance || 0)).catch(e=>{
      });
    });
  }, [showAdd]);
  const [toast, setToast] = React.useState('');
  const [expiring, setExpiring] = React.useState([]);
  const [tierData, setTierData] = React.useState(null);
  const showToast = (m) => { setToast(m); setTimeout(()=>setToast(''),3000); };

  const loadData = async () => {
    setLoading(true);
    try {
      const [myC, plans, exp, tier] = await Promise.all([
        providerApi.myCategories(),
        providerApi.categories(),
        providerApi.expiring().catch(()=>({expiring:[]})),
        providerApi.tier().catch(()=>null),
      ]);
      const myCats = myC.categories||[];
      setCats(myCats);
      setAllPlans(plans.plans||[]);
      // Auto-trigger onboarding if first category has no profile
      if (myCats.length > 0) {
        const firstCat = myCats[0];
        providerApi.getProfile(firstCat.category_id).then(p=>{
          if (!p?.display_name) {
            const match = (plans.plans||[]).find(pl=>pl.id===firstCat.category_id);
            if (match) setOnboardingCat(match);
          }
        }).catch(()=>{});
      }
      setExpiring(exp.expiring||[]);
      setTierData(tier);
    } catch(e){}
    setLoading(false);
  };

  const handleRenew = async (categoryId, name) => {
    try {
      await providerApi.renew({category_id: categoryId});
      showToast(`✓ ${name} renewed for 30 days!`);
      loadData();
    } catch(e) { showToast('❌ '+(e.message||'Renewal failed')); }
  };

  React.useEffect(() => { loadData(); }, []);

  const handleSubscribe = async (plan) => {
    try {
      await providerApi.subscribe({category_id: plan.id});
      showToast(`✓ ${plan.name} business started!`);
      loadData();
      setShowAdd(false);
    } catch(e) { showToast('❌ '+(e.message||'Failed')); }
  };

  if (loading) return (
    <div style={{textAlign:'center',padding:40}}>
      <div style={{width:32,height:32,border:`3px solid ${C.border}`,borderTop:`3px solid #0891b2`,borderRadius:'50%',margin:'0 auto',animation:'spin 0.8s linear infinite'}}/>
    </div>
  );

  // Show analytics view
  if (selectedCat?.showAnalytics) {
    return <AnalyticsView C={C} onBack={()=>setSelectedCat(null)} />;
  }

  // Show tiers detail view
  if (selectedCat?.showTiers) {
    return (
      <div>
        <button onClick={()=>setSelectedCat(null)} style={{background:C.input,border:`1px solid ${C.border}`,borderRadius:12,padding:'8px 16px',color:C.text,fontSize:13,fontWeight:600,cursor:'pointer',marginBottom:16,display:'flex',alignItems:'center',gap:6}}>
          ← My Business
        </button>
        <div style={{fontSize:18,fontWeight:800,color:C.text,marginBottom:16}}>Provider Tiers</div>
        {tierData?.tiers.map((t,i)=>(
          <div key={i} style={{
            background:t.is_current?t.color+'18':C.card,
            border:t.is_current?`2px solid ${t.color}`:`1px solid ${C.border}`,
            borderRadius:18,padding:'16px',marginBottom:12,
          }}>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:10}}>
              <div style={{fontSize:32}}>{t.icon}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:16,fontWeight:800,color:t.color}}>{t.name}</div>
                <div style={{fontSize:11,color:C.muted}}>Platform fee: {t.platform_fee_pct}%</div>
              </div>
              {t.is_current && (
                <span style={{background:t.color,color:'#fff',padding:'3px 10px',borderRadius:8,fontSize:10,fontWeight:700}}>CURRENT</span>
              )}
            </div>
            <div style={{fontSize:11,color:C.muted,marginBottom:8,lineHeight:1.6}}>
              {t.min_orders > 0 && <div>• {t.min_orders}+ completed orders</div>}
              {t.min_rating > 0 && <div>• {t.min_rating}+ average rating</div>}
              {t.requires_kyc && <div>• KYC verification required</div>}
              {!t.min_orders && !t.requires_kyc && <div>• Starting tier (no requirements)</div>}
            </div>
            <div style={{background:C.input,borderRadius:10,padding:'10px 12px'}}>
              <div style={{fontSize:10,color:C.muted,fontWeight:700,marginBottom:4,textTransform:'uppercase'}}>Benefits</div>
              {(t.benefits||[]).map((b,j)=>(
                <div key={j} style={{fontSize:11,color:C.text,marginBottom:3}}>✓ {b}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  // Show category dashboard
  if (selectedCat) {
    return (
      <div>
        <button onClick={()=>setSelectedCat(null)} style={{background:C.input,border:`1px solid ${C.border}`,borderRadius:12,padding:'8px 16px',color:C.text,fontSize:13,fontWeight:600,cursor:'pointer',marginBottom:16,display:'flex',alignItems:'center',gap:6}}>
          ← My Business
        </button>
        <CategoryDashboard C={C} nav={nav} category={selectedCat} />
      </div>
    );
  }

  return (
    <div>
      {toast && (
        <div style={{position:'fixed',top:80,left:'50%',transform:'translateX(-50%)',background:'#1a1a2e',color:'#fff',padding:'10px 20px',borderRadius:20,zIndex:99999,fontSize:13,fontWeight:600}}>
          {toast}
        </div>
      )}

      {/* Tier card */}
      {tierData && (
        <div style={{
          background:`linear-gradient(135deg, ${tierData.tiers.find(t=>t.is_current)?.color||'#a16207'}18, ${tierData.tiers.find(t=>t.is_current)?.color||'#a16207'}08)`,
          border:`1px solid ${tierData.tiers.find(t=>t.is_current)?.color||'#a16207'}44`,
          borderRadius:18,padding:'16px',marginBottom:14,
        }}>
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:10}}>
            <div style={{fontSize:36}}>{tierData.tiers.find(t=>t.is_current)?.icon}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:12,color:C.muted,fontWeight:600,textTransform:'uppercase'}}>Current Tier</div>
              <div style={{fontSize:18,fontWeight:800,color:tierData.tiers.find(t=>t.is_current)?.color}}>
                {tierData.tiers.find(t=>t.is_current)?.name}
              </div>
            </div>
            <button onClick={()=>{setSelectedCat({showTiers:true});}} style={{
              background:'transparent',border:`1px solid ${C.border}`,
              borderRadius:10,padding:'6px 12px',color:C.muted,
              fontSize:11,fontWeight:600,cursor:'pointer',
            }}>View All →</button>
          </div>
          
          {/* Stats */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:10}}>
            <div style={{textAlign:'center',padding:'8px',background:C.card,borderRadius:10}}>
              <div style={{fontSize:16,fontWeight:800,color:C.text}}>{tierData.stats.completed_orders}</div>
              <div style={{fontSize:10,color:C.muted}}>Orders</div>
            </div>
            <div style={{textAlign:'center',padding:'8px',background:C.card,borderRadius:10}}>
              <div style={{fontSize:16,fontWeight:800,color:C.text}}>{tierData.stats.avg_rating.toFixed(1)}</div>
              <div style={{fontSize:10,color:C.muted}}>★ Rating</div>
            </div>
            <div style={{textAlign:'center',padding:'8px',background:C.card,borderRadius:10}}>
              <div style={{fontSize:16,fontWeight:800,color:tierData.stats.kyc_verified?'#10b981':C.muted}}>
                {tierData.stats.kyc_verified?'✓':'✗'}
              </div>
              <div style={{fontSize:10,color:C.muted}}>KYC</div>
            </div>
          </div>

          {/* Next tier progress */}
          {tierData.next_tier && (
            <div style={{padding:'10px 12px',background:C.card,borderRadius:12}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                <span style={{fontSize:18}}>{tierData.next_tier.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,color:C.muted}}>Next: <span style={{color:tierData.next_tier.color,fontWeight:700}}>{tierData.next_tier.name}</span></div>
                </div>
              </div>
              <div style={{fontSize:10,color:C.muted,lineHeight:1.5}}>
                {tierData.next_tier.requires_kyc && !tierData.stats.kyc_verified && <div>• Complete KYC verification</div>}
                {tierData.next_tier.min_orders > tierData.stats.completed_orders && (
                  <div>• Complete {tierData.next_tier.min_orders - tierData.stats.completed_orders} more orders</div>
                )}
                {tierData.next_tier.min_rating > tierData.stats.avg_rating && (
                  <div>• Achieve {tierData.next_tier.min_rating}+ rating (current: {tierData.stats.avg_rating.toFixed(1)})</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Renewal banner */}
      {expiring.length > 0 && (
        <div style={{background:'#f9731618',border:'1px solid #f9731633',borderRadius:16,padding:'14px 16px',marginBottom:16}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
            <span style={{fontSize:20}}>⏰</span>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:700,color:'#f97316'}}>
                {expiring.length} subscription{expiring.length>1?'s':''} expiring soon
              </div>
              <div style={{fontSize:11,color:C.muted}}>Renew to keep receiving requests</div>
            </div>
          </div>
          {expiring.map((exp,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:10,background:C.card,borderRadius:12,padding:'10px 12px',marginBottom:i<expiring.length-1?8:0}}>
              <div style={{fontSize:22}}>{exp.icon}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:C.text}}>{exp.name}</div>
                <div style={{fontSize:10,color:exp.days_left<=3?'#ef4444':'#f97316',fontWeight:600}}>
                  Expires in {exp.days_left} day{exp.days_left!==1?'s':''}
                </div>
              </div>
              <button onClick={()=>handleRenew(exp.category_id, exp.name)} style={{
                background:'#f97316',border:'none',borderRadius:10,padding:'6px 14px',
                color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer',
              }}>
                Renew {exp.price_txc} TXC
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:8,marginBottom:16}}>
        <button onClick={()=>setShowAdd(true)} style={{
          background:C.primary,border:'none',borderRadius:14,
          padding:'14px',color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer',
          boxShadow:`0 4px 20px ${C.primary}44`,
        }}>
          + Start New Business
        </button>
        <button onClick={()=>setSelectedCat({showAnalytics:true})} style={{
          background:'#7c3aed18',border:`1px solid #7c3aed33`,borderRadius:14,
          padding:'14px',color:'#7c3aed',fontSize:13,fontWeight:700,cursor:'pointer',
        }}>
          📊 Analytics
        </button>
      </div>

      {/* My businesses list */}
      {cats.length === 0 ? (
        <div style={{textAlign:'center',padding:'40px 20px',background:C.card,borderRadius:20,border:`1px solid ${C.border}`}}>
          <div style={{fontSize:48,marginBottom:12}}>🏪</div>
          <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:8}}>No Businesses Yet</div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.5}}>
            Start a business by subscribing to a category.<br/>
            Customers can then find and order from you.
          </div>
        </div>
      ) : cats.map((cat,i)=>(
        <div key={i} onClick={()=>{const route={auctions:"/mod/mod_auction",rentals:"/mod/mod_rentals",work:"/mod/mod_find_work",shop:"/mod/mod_shop",talent_hire:"/mod/mod_find_job"}[cat.category_id||cat.id];if(route){sessionStorage.setItem("back_url","/explore-sections?tab=business");nav(route);}else{setSelectedCat(cat);}}} style={{
          background:C.card,border:`1px solid #0891b244`,
          borderRadius:16,padding:'14px 16px',marginBottom:10,
          cursor:'pointer',boxShadow:C.shadow,
          display:'flex',alignItems:'center',gap:12,
        }}>
          <div style={{width:52,height:52,borderRadius:16,background:'#0891b218',display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,flexShrink:0}}>{cat.icon}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:3}}>{cat.name}</div>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>
              {cat.service_count} services · {cat.price_txc} TXC/month
            </div>
            <div style={{display:'flex',gap:5}}>
              <span style={{background:'#10b98118',color:'#10b981',padding:'2px 8px',borderRadius:6,fontSize:9,fontWeight:700}}>✓ Active</span>
              <span style={{fontSize:9,color:C.muted,alignSelf:'center'}}>
                expires {new Date(cat.expires_at).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}
              </span>
            </div>
          </div>
          <div style={{fontSize:20,color:C.muted}}>→</div>
        </div>
      ))}

      {/* Subscribe modal */}
      {showAdd && (
        <div onClick={()=>{setShowAdd(false);setBizSearch('');setConfirmPlan(null);}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:10000,display:'flex',alignItems:'flex-end',justifyContent:'center',backdropFilter:'blur(4px)'}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.bg,borderRadius:'24px 24px 0 0',width:'100%',maxWidth:430,height:'85vh',maxHeight:'85vh',display:'flex',flexDirection:'column',boxShadow:'0 -8px 40px rgba(0,0,0,0.3)'}}>
            
            {/* Drag handle */}
            <div style={{padding:'10px 0 0 0',display:'flex',justifyContent:'center',flexShrink:0}}>
              <div style={{width:40,height:4,background:C.border,borderRadius:2}}/>
            </div>
            
            {/* Header with close */}
            <div style={{padding:'12px 16px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
                <div style={{fontSize:17,fontWeight:800,color:C.text}}>Start New Business</div>
                <button onClick={()=>{setShowAdd(false);setBizSearch('');setConfirmPlan(null);}} style={{background:C.input,border:`1px solid ${C.border}`,borderRadius:'50%',width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,color:C.muted,cursor:'pointer'}}>×</button>
              </div>
              <div style={{fontSize:11,color:C.muted,marginBottom:10}}>Subscribe to a category · 30-day access · Your balance: <b style={{color:C.text}}>{Math.round(userBal).toLocaleString()} TXC</b></div>
              <div style={{position:'relative'}}>
                <input type="text" placeholder="Search by name or keyword..." value={bizSearch} onChange={e=>setBizSearch(e.target.value)} style={{width:'100%',padding:'10px 36px 10px 36px',background:C.input,border:`1px solid ${C.border}`,borderRadius:10,color:C.text,fontSize:13,outline:'none',boxSizing:'border-box'}} autoFocus />
                <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:14,color:C.muted}}>🔍</span>
                {bizSearch && (
                  <button onClick={()=>setBizSearch('')} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'transparent',border:'none',fontSize:16,color:C.muted,cursor:'pointer',padding:4}}>×</button>
                )}
              </div>
            </div>
            <div style={{overflowY:'auto',flex:1,padding:16}}>
              {allPlans.filter(p=>!cats.find(c=>c.category_id===p.id)).filter(p=>{
                if (!bizSearch) return true;
                const q = bizSearch.toLowerCase();
                return p.name.toLowerCase().includes(q) || (p.description||'').toLowerCase().includes(q);
              }).map((plan,i)=>(
                <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:'12px 14px',marginBottom:10,display:'flex',alignItems:'center',gap:12}}>
                  <div style={{fontSize:28,flexShrink:0}}>{plan.icon}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{plan.name}</div>
                    <div style={{fontSize:10,color:C.muted}}>{plan.description}</div>
                  </div>
                  <button onClick={()=>setConfirmPlan(plan)} disabled={userBal < plan.price_txc} style={{background: userBal < plan.price_txc ? C.border : '#0891b2',border:'none',borderRadius:10,padding:'8px 14px',color:'#fff',fontSize:12,fontWeight:700,cursor:userBal < plan.price_txc ? 'not-allowed' : 'pointer',flexShrink:0,minWidth:85}}>
                    {userBal < plan.price_txc ? '🔒 Low bal' : `${plan.price_txc} TXC`}
                  </button>
                </div>
              ))}
              {(() => {
                const filtered = allPlans.filter(p=>!cats.find(c=>c.category_id===p.id)).filter(p=>{
                  if (!bizSearch) return true;
                  const q = bizSearch.toLowerCase();
                  return p.name.toLowerCase().includes(q) || (p.description||'').toLowerCase().includes(q);
                });
                if (filtered.length === 0) {
                  return (
                    <div style={{textAlign:'center',padding:24,color:C.muted,fontSize:13}}>
                      {bizSearch ? `No categories matching "${bizSearch}"` : 'All categories subscribed!'}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
          
          {/* Confirmation overlay */}
          {confirmPlan && (
            <div onClick={(e)=>{e.stopPropagation();setConfirmPlan(null);}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:10001,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
              <div onClick={e=>e.stopPropagation()} style={{background:C.card,borderRadius:20,padding:'20px',width:'100%',maxWidth:360}}>
                <div style={{textAlign:'center',marginBottom:14}}>
                  <div style={{fontSize:42,marginBottom:8}}>{confirmPlan.icon}</div>
                  <div style={{fontSize:17,fontWeight:800,color:C.text}}>Subscribe to {confirmPlan.name}?</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:4}}>{confirmPlan.description}</div>
                </div>
                
                <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:12,padding:'12px',marginBottom:14}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:6}}>
                    <span style={{color:C.muted}}>Price</span>
                    <span style={{color:C.text,fontWeight:700}}>{confirmPlan.price_txc} TXC</span>
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:6}}>
                    <span style={{color:C.muted}}>Duration</span>
                    <span style={{color:C.text,fontWeight:700}}>30 days</span>
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:12}}>
                    <span style={{color:C.muted}}>Your balance after</span>
                    <span style={{color:userBal - confirmPlan.price_txc >= 0 ? '#10b981' : '#ef4444',fontWeight:700}}>
                      {(userBal - confirmPlan.price_txc).toFixed(0)} TXC
                    </span>
                  </div>
                </div>
                
                <div style={{fontSize:10,color:C.muted,background:'#f59e0b18',border:'1px solid #f59e0b33',borderRadius:10,padding:'8px 10px',marginBottom:12}}>
                  ℹ️ You can add services, accept orders, and receive payments for 30 days. Auto-renew available.
                </div>
                
                <div style={{display:'flex',gap:8}}>
                  <button onClick={()=>setConfirmPlan(null)} disabled={subscribing} style={{flex:1,background:C.input,border:`1px solid ${C.border}`,borderRadius:12,padding:'12px',color:C.text,fontSize:13,fontWeight:700,cursor:subscribing?'not-allowed':'pointer'}}>
                    Cancel
                  </button>
                  <button onClick={async()=>{
                    setSubscribing(true);
                    try{
                      await handleSubscribe(confirmPlan);
                      setConfirmPlan(null);
                    } catch(e) {}
                    setSubscribing(false);
                  }} disabled={subscribing} style={{flex:1.5,background:subscribing?C.border:'#0891b2',border:'none',borderRadius:12,padding:'12px',color:'#fff',fontSize:13,fontWeight:800,cursor:subscribing?'wait':'pointer'}}>
                    {subscribing ? '⏳ Processing...' : `Pay ${confirmPlan.price_txc} TXC`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────
// CATEGORY DASHBOARD - per-category business management
// ────────────────────────────────────────────────────────
function CategoryDashboard({ C, nav, category }) {
  const [ptab, setPtab] = React.useState('requests');
  const [requests, setRequests] = React.useState([]);
  const [orders, setOrders] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showAddSvc, setShowAddSvc] = React.useState(false);
  const [editSvc, setEditSvc] = React.useState(null);
  const [svcForm, setSvcForm] = React.useState({name:'',description:'',price_txc:'',unit:'per service', images: []});
  const [accepting, setAccepting] = React.useState(null);
  const [toast, setToast] = React.useState('');
  const [filters, setFilters] = React.useState({q:'',location:'',sort:'recent',min_budget:'',max_budget:''});
  const [showFilters, setShowFilters] = React.useState(false);
  const showToast = (m) => { setToast(m); setTimeout(()=>setToast(''),3000); };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [reqs, ords, svcs] = await Promise.all([
        providerApi.browseRequests({
          category: category.category_id,
          q: filters.q,
          location: filters.location,
          min_budget: filters.min_budget,
          max_budget: filters.max_budget,
          sort: filters.sort,
        }),
        providerApi.myOrders(),
        providerApi.myServices(),
      ]);
      setRequests(reqs.requests||[]);
      setOrders((ords.orders||[]).filter(o => o.category === category.category_id));
      setServices((svcs.services||[]).filter(s => s.category_id === category.category_id));
    } catch(e){}
    setLoading(false);
  };

  React.useEffect(() => { loadAll(); }, [category.category_id, filters]);

  const handleAccept = async (reqId) => {
    setAccepting(reqId);
    try {
      await providerApi.acceptRequest({request_id: reqId});
      showToast('✓ Request accepted!');
      loadAll();
      setPtab('orders');
    } catch(e) { showToast('❌ '+(e.message||'Failed')); }
    setAccepting(null);
  };

  const handleSaveService = async () => {
    if (!svcForm.name || !svcForm.price_txc) { showToast('Fill required fields'); return; }
    try {
      if (editSvc) {
        await providerApi.updateService(editSvc.id, {...svcForm, category_id: category.category_id, price_txc: parseFloat(svcForm.price_txc), is_active: true});
        showToast('✓ Updated!');
      } else {
        await providerApi.addService({...svcForm, category_id: category.category_id, price_txc: parseFloat(svcForm.price_txc)});
        showToast('✓ Added!');
      }
      loadAll();
      setShowAddSvc(false); setEditSvc(null);
      setSvcForm({name:'',description:'',price_txc:'',unit:'per service'});
    } catch(e) { showToast('❌ '+(e.message||'Failed')); }
  };

  const handleDelete = async (id) => {
    try {
      await providerApi.deleteService(id);
      showToast('✓ Deleted');
      loadAll();
    } catch(e) { showToast('❌ '+(e.message||'Failed')); }
  };

  const statusColor = (s) => ({
    open:'#10b981', accepted:'#1a3cc8', in_progress:'#f97316',
    completed:'#7c3aed', cancelled:'#ef4444',
  }[s]||'#9ca3af');

  if (loading) return (
    <div style={{textAlign:'center',padding:40}}>
      <div style={{width:32,height:32,border:`3px solid ${C.border}`,borderTop:`3px solid #0891b2`,borderRadius:'50%',margin:'0 auto',animation:'spin 0.8s linear infinite'}}/>
    </div>
  );

  return (
    <div>
      {toast && (
        <div style={{position:'fixed',top:80,left:'50%',transform:'translateX(-50%)',background:'#1a1a2e',color:'#fff',padding:'10px 20px',borderRadius:20,zIndex:99999,fontSize:13,fontWeight:600}}>
          {toast}
        </div>
      )}

      {/* Category header */}
      <div style={{background:'#0891b218',border:`1px solid #0891b233`,borderRadius:16,padding:'14px 16px',marginBottom:14,display:'flex',alignItems:'center',gap:12}}>
        <div style={{fontSize:32}}>{category.icon}</div>
        <div style={{flex:1}}>
          <div style={{fontSize:15,fontWeight:800,color:C.text}}>{category.name}</div>
          <div style={{fontSize:11,color:C.muted}}>
            Active subscription · {category.price_txc} TXC/month
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',background:C.input,borderRadius:14,padding:3,marginBottom:16,gap:2}}>
        {[
          {id:'requests', label:'Requests', count:requests.length},
          {id:'orders',   label:'Orders',   count:orders.length},
          {id:'services', label:'Services', count:services.length},
        ].map(t=>(
          <button key={t.id} onClick={()=>setPtab(t.id)} style={{
            flex:1,padding:'9px 4px',borderRadius:11,border:'none',cursor:'pointer',
            background:ptab===t.id?'#0891b2':'transparent',
            color:ptab===t.id?'#fff':C.muted,
            fontSize:12,fontWeight:ptab===t.id?700:500,
            position:'relative',
          }}>
            {t.label}
            {t.count>0 && <span style={{
              position:'absolute',top:4,right:4,
              background:ptab===t.id?'rgba(255,255,255,0.3)':'#ef4444',
              color:'#fff',borderRadius:10,fontSize:9,fontWeight:700,
              padding:'1px 5px',minWidth:16,textAlign:'center',
            }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* REQUESTS */}
      {ptab==='requests' && (
        <>
          {/* Filter bar */}
          <div style={{display:'flex',gap:8,marginBottom:12}}>
            <input type="text" placeholder="Search requests..." value={filters.q}
              onChange={e=>setFilters(p=>({...p,q:e.target.value}))}
              style={{flex:1,padding:'8px 12px',background:C.input,border:`1px solid ${C.border}`,borderRadius:10,color:C.text,fontSize:12,outline:'none',boxSizing:'border-box'}}
            />
            <button onClick={()=>setShowFilters(!showFilters)} style={{
              background:showFilters?C.primary:C.input,border:`1px solid ${showFilters?C.primary:C.border}`,
              borderRadius:10,padding:'8px 12px',color:showFilters?'#fff':C.text,fontSize:12,fontWeight:600,cursor:'pointer',
            }}>
              ⚙ Filters {(filters.location||filters.min_budget||filters.max_budget||filters.sort!=='recent')?'•':''}
            </button>
          </div>
          
          {showFilters && (
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:'12px',marginBottom:12}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                <input type="text" placeholder="Location" value={filters.location}
                  onChange={e=>setFilters(p=>({...p,location:e.target.value}))}
                  style={{padding:'8px 10px',background:C.input,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,fontSize:11,outline:'none',boxSizing:'border-box'}}
                />
                <select value={filters.sort} onChange={e=>setFilters(p=>({...p,sort:e.target.value}))}
                  style={{padding:'8px 10px',background:C.input,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,fontSize:11,outline:'none',boxSizing:'border-box'}}>
                  <option value="recent">Most Recent</option>
                  <option value="budget_high">Budget: High to Low</option>
                  <option value="budget_low">Budget: Low to High</option>
                  <option value="popular">Most Quoted</option>
                </select>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                <input type="number" placeholder="Min budget (TXC)" value={filters.min_budget}
                  onChange={e=>setFilters(p=>({...p,min_budget:e.target.value}))}
                  style={{padding:'8px 10px',background:C.input,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,fontSize:11,outline:'none',boxSizing:'border-box'}}
                />
                <input type="number" placeholder="Max budget (TXC)" value={filters.max_budget}
                  onChange={e=>setFilters(p=>({...p,max_budget:e.target.value}))}
                  style={{padding:'8px 10px',background:C.input,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,fontSize:11,outline:'none',boxSizing:'border-box'}}
                />
              </div>
              <button onClick={()=>setFilters({q:'',location:'',sort:'recent',min_budget:'',max_budget:''})} style={{
                width:'100%',background:C.input,border:`1px solid ${C.border}`,borderRadius:8,
                padding:'6px',color:C.muted,fontSize:11,fontWeight:600,cursor:'pointer',
              }}>Clear All Filters</button>
            </div>
          )}
          
          {requests.length===0 ? (
            <div style={{textAlign:'center',padding:'30px 20px',background:C.card,borderRadius:18,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:40,marginBottom:10}}>🔍</div>
              <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:4}}>No Open Requests</div>
              <div style={{fontSize:11,color:C.muted}}>New {category.name.toLowerCase()} requests will appear here</div>
            </div>
          ) : requests.map((req,i)=>(
          <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:'14px 16px',marginBottom:10,boxShadow:C.shadow}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
              <div style={{flex:1,paddingRight:8}}>
                <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:3}}>{req.title}</div>
                <div style={{fontSize:11,color:C.muted,marginBottom:4}}>{req.location||'Location not specified'} · {req.quote_count} quotes</div>
                {req.description && <div style={{fontSize:11,color:C.muted,lineHeight:1.4}}>{req.description.slice(0,100)}{req.description.length>100?'...':''}</div>}
              </div>
              <div style={{textAlign:'right',flexShrink:0}}>
                {req.budget>0 && <div style={{fontSize:14,fontWeight:800,color:'#10b981'}}>{req.budget} TXC</div>}
              </div>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontSize:10,color:C.muted}}>{new Date(req.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</div>
              <button onClick={()=>handleAccept(req.id)} disabled={accepting===req.id} style={{
                background:accepting===req.id?C.muted:'#10b981',border:'none',
                borderRadius:10,padding:'8px 20px',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer',
              }}>
                {accepting===req.id?'Accepting...':'✓ Accept'}
              </button>
            </div>
            </div>
          ))}
        </>
      )}

      {/* ORDERS */}
      {ptab==='orders' && (
        orders.length===0 ? (
          <div style={{textAlign:'center',padding:'30px 20px',background:C.card,borderRadius:18,border:`1px solid ${C.border}`}}>
            <div style={{fontSize:40,marginBottom:10}}>📦</div>
            <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:4}}>No Orders Yet</div>
            <div style={{fontSize:11,color:C.muted}}>Accepted requests will appear here</div>
          </div>
        ) : orders.map((o,i)=>(
          <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:'14px 16px',marginBottom:10,boxShadow:C.shadow}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:3}}>{o.title}</div>
                <div style={{fontSize:11,color:C.muted}}>{o.buyer_name||'Buyer'}</div>
              </div>
              <div style={{textAlign:'right'}}>
                {o.amount>0 && <div style={{fontSize:14,fontWeight:800,color:'#10b981'}}>{Math.round(o.amount).toLocaleString()} TXC</div>}
              </div>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{background:statusColor(o.status)+'18',color:statusColor(o.status),padding:'3px 10px',borderRadius:8,fontSize:10,fontWeight:700}}>
                {o.status.toUpperCase()}
              </span>
              <div style={{display:'flex',gap:6}}>
                {o.status==='accepted' && (
                  <button onClick={async()=>{ await providerApi.updateOrder(o.id,{status:'in_progress'}); loadAll(); showToast('✓ Started'); }} style={{background:'#f9731618',border:'1px solid #f9731633',borderRadius:8,padding:'5px 10px',color:'#f97316',fontSize:10,fontWeight:700,cursor:'pointer'}}>
                    Start Work
                  </button>
                )}
                {o.status==='in_progress' && (
                  <button onClick={async()=>{ await providerApi.updateOrder(o.id,{status:'completed'}); loadAll(); showToast('✓ Completed'); }} style={{background:'#10b98118',border:'1px solid #10b98133',borderRadius:8,padding:'5px 10px',color:'#10b981',fontSize:10,fontWeight:700,cursor:'pointer'}}>
                    Mark Done
                  </button>
                )}
              </div>
            </div>
          </div>
        ))
      )}

      {/* SERVICES */}
      {ptab==='services' && (
        <div>
          <button onClick={()=>{setShowAddSvc(true);setEditSvc(null);setSvcForm({name:'',description:'',price_txc:'',unit:'per service'});}} style={{width:'100%',background:'#10b981',border:'none',borderRadius:12,padding:'11px',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',marginBottom:12}}>
            + Add Service
          </button>

          {services.length===0 ? (
            <div style={{textAlign:'center',padding:'30px 20px',background:C.card,borderRadius:18,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:40,marginBottom:10}}>🛠️</div>
              <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:4}}>No Services Yet</div>
              <div style={{fontSize:11,color:C.muted}}>Add services to attract customers</div>
            </div>
          ) : services.map((svc,i)=>(
            <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:'14px 16px',marginBottom:10,boxShadow:C.shadow}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:3}}>{svc.name}</div>
                  {svc.description && <div style={{fontSize:11,color:C.muted}}>{svc.description}</div>}
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:14,fontWeight:800,color:'#10b981'}}>{svc.price_txc} TXC</div>
                  <div style={{fontSize:10,color:C.muted}}>{svc.unit}</div>
                </div>
              </div>
              <div style={{display:'flex',gap:6,marginTop:10}}>
                <button onClick={()=>{setEditSvc(svc);setSvcForm({name:svc.name,description:svc.description||'',price_txc:svc.price_txc,unit:svc.unit||'per service'});setShowAddSvc(true);}} style={{flex:1,background:C.input,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px',color:C.text,fontSize:11,fontWeight:600,cursor:'pointer'}}>
                  ✏️ Edit
                </button>
                <button onClick={()=>handleDelete(svc.id)} style={{flex:1,background:'#ef444412',border:'1px solid #ef444433',borderRadius:8,padding:'6px',color:'#ef4444',fontSize:11,fontWeight:600,cursor:'pointer'}}>
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}

          {/* Service modal — Dynamic */}
          {showAddSvc && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:10000,
              display:'flex',alignItems:'flex-end',justifyContent:'center',padding:'0'}}
              onClick={()=>setShowAddSvc(false)}>
              <div onClick={e=>e.stopPropagation()} style={{
                background:C.bg,borderRadius:'24px 24px 0 0',width:'100%',
                maxWidth:430,maxHeight:'85vh',display:'flex',flexDirection:'column',
                boxShadow:'0 -4px 30px rgba(0,0,0,0.2)'}}>
                <div style={{padding:'14px 16px 8px',flexShrink:0,borderBottom:`1px solid ${C.border}`}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div style={{fontSize:14,fontWeight:800,color:C.text}}>
                      {editSvc?'Edit':'Add'} Service — {category.icon} {category.name}
                    </div>
                    <button onClick={()=>setShowAddSvc(false)} style={{background:'none',border:'none',
                      fontSize:20,color:C.muted,cursor:'pointer',lineHeight:1}}>×</button>
                  </div>
                </div>
                <div style={{overflowY:'auto',flex:1,padding:'16px 16px 8px'}}>
                  <DynamicServiceForm
                    category={category}
                    editData={editSvc ? {...editSvc,...(editSvc.tags||{})} : null}
                    onSave={async(formData)=>{
                      const payload = {
                        category_id: category.id,
                        name: formData.name,
                        description: formData.description||'',
                        price_txc: parseFloat(formData.price_txc)||0,
                        unit: formData.unit||'per service',
                        tags: formData.tags||{},
                      };
                      try {
                        if (editSvc) {
                          await providerApi.updateService(editSvc.id,{...payload,is_active:true});
                        } else {
                          await providerApi.addService(payload);
                        }
                        showToast('✓ Service saved!');
                        setShowAddSvc(false);
                        loadAll();
                      } catch(e){ showToast('❌ '+(e.message||'Failed')); }
                    }}
                    onCancel={()=>setShowAddSvc(false)}
                    C={C}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────
// ANALYTICS VIEW
// ────────────────────────────────────────────────────────
function AnalyticsView({ C, onBack }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    providerApi.analytics()
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{textAlign:'center',padding:40}}>
      <div style={{width:32,height:32,border:`3px solid ${C.border}`,borderTop:`3px solid #7c3aed`,borderRadius:'50%',margin:'0 auto',animation:'spin 0.8s linear infinite'}}/>
    </div>
  );

  if (!data) return <div style={{textAlign:'center',padding:40,color:C.muted}}>No data</div>;

  const { overall, by_category, timeline_30d } = data;
  const maxEarned = Math.max(1, ...timeline_30d.map(t => t.earned));

  return (
    <div>
      <button onClick={onBack} style={{background:C.input,border:`1px solid ${C.border}`,borderRadius:12,padding:'8px 16px',color:C.text,fontSize:13,fontWeight:600,cursor:'pointer',marginBottom:16,display:'flex',alignItems:'center',gap:6}}>
        ← My Business
      </button>

      <div style={{fontSize:18,fontWeight:800,color:C.text,marginBottom:16}}>📊 Business Analytics</div>

      {/* Overall stats */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
        <div style={{background:'#10b98118',border:'1px solid #10b98133',borderRadius:14,padding:'14px'}}>
          <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:'uppercase',marginBottom:4}}>Total Earned</div>
          <div style={{fontSize:22,fontWeight:900,color:'#10b981'}}>{overall.total_earned.toFixed(0)}</div>
          <div style={{fontSize:10,color:C.muted}}>TXC from completed orders</div>
        </div>
        <div style={{background:'#1a3cc818',border:'1px solid #1a3cc833',borderRadius:14,padding:'14px'}}>
          <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:'uppercase',marginBottom:4}}>Avg Order</div>
          <div style={{fontSize:22,fontWeight:900,color:'#1a3cc8'}}>{overall.avg_order_value.toFixed(0)}</div>
          <div style={{fontSize:10,color:C.muted}}>TXC per order</div>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:16}}>
        {[
          {label:'Total',       value:overall.total_orders, color:'#7c3aed'},
          {label:'Accepted',    value:overall.accepted,     color:'#1a3cc8'},
          {label:'In Progress', value:overall.in_progress,  color:'#f97316'},
          {label:'Completed',   value:overall.completed,    color:'#10b981'},
        ].map((s,i)=>(
          <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:'10px 6px',textAlign:'center'}}>
            <div style={{fontSize:18,fontWeight:900,color:s.color}}>{s.value}</div>
            <div style={{fontSize:9,color:C.muted,fontWeight:600}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Timeline chart */}
      {timeline_30d.length > 0 && (
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:'16px',marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:12}}>Last 30 Days Activity</div>
          <div style={{display:'flex',alignItems:'flex-end',gap:2,height:100,marginBottom:8}}>
            {timeline_30d.map((t,i) => (
              <div key={i} title={`${t.day}: ${t.orders} orders, ${t.earned} TXC`}
                style={{
                  flex:1,
                  background:t.earned>0?'#10b981':C.border,
                  borderRadius:'3px 3px 0 0',
                  height:`${Math.max(4, (t.earned/maxEarned)*100)}%`,
                  minHeight:4,
                  cursor:'pointer',
                }}
              />
            ))}
          </div>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:9,color:C.muted}}>
            <span>{timeline_30d[0]?.day}</span>
            <span>{timeline_30d[timeline_30d.length-1]?.day}</span>
          </div>
        </div>
      )}

      {/* Per-category */}
      {by_category.length > 0 && (
        <div>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:10}}>By Business Category</div>
          {by_category.map((cat,i) => (
            <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:'12px 14px',marginBottom:8}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                <div style={{fontSize:24}}>{cat.icon||'◈'}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.text}}>{cat.name}</div>
                  <div style={{fontSize:10,color:C.muted}}>
                    {cat.orders} orders · {cat.completed} completed
                  </div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:14,fontWeight:800,color:'#10b981'}}>{cat.earned.toFixed(0)}</div>
                  <div style={{fontSize:9,color:C.muted}}>TXC earned</div>
                </div>
              </div>
              <div style={{width:'100%',height:4,background:C.input,borderRadius:2,overflow:'hidden'}}>
                <div style={{
                  width:`${(cat.completed/Math.max(1,cat.orders))*100}%`,
                  height:'100%',
                  background:'#10b981',
                  borderRadius:2,
                }}/>
              </div>
              <div style={{fontSize:9,color:C.muted,marginTop:3}}>
                {Math.round((cat.completed/Math.max(1,cat.orders))*100)}% completion rate
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function RoleActivationModal({ role, C, balance, onClose, onActivated }) {
  const [fields, setFields] = React.useState([]);
  const [formData, setFormData] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [activating, setActivating] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    rolesConfig.fields(role.id)
      .then(r => {
        setFields(r.fields || []);
        // Initialize form with empty values
        const init = {};
        (r.fields || []).forEach(f => { init[f.key] = ''; });
        setFormData(init);
      })
      .catch(() => setFields([]))
      .finally(() => setLoading(false));
  }, [role.id]);

  const handleActivate = async () => {
    // Validate required fields
    const missing = fields.filter(f => f.required && !formData[f.key]);
    if (missing.length > 0) {
      setError(`Please fill: ${missing.map(f=>f.label).join(', ')}`);
      return;
    }
    // Check balance
    if (role.price_txc > 0 && balance.txc < role.price_txc) {
      setError(`Insufficient balance. Need ${role.price_txc} TXC, you have ${Math.round(balance.txc).toLocaleString()} TXC`);
      return;
    }
    setActivating(true);
    setError('');
    try {
      await rolesConfig.activate({
        role_id: role.id,
        role_details: formData,
      });
      onActivated(role);
    } catch(e) {
      setError(e.message || 'Activation failed');
    }
    setActivating(false);
  };

  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:10000,display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:C.bg,borderRadius:'24px 24px 0 0',
        width:'100%',maxWidth:430,maxHeight:'92vh',
        display:'flex',flexDirection:'column',
      }}>
        {/* Header */}
        <div style={{
          display:'flex',alignItems:'center',gap:12,
          padding:'16px 16px 12px',
          borderBottom:`1px solid ${C.border}`,
          flexShrink:0,
        }}>
          <div style={{width:44,height:44,borderRadius:14,background:role.color+'22',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>{role.icon}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:16,fontWeight:800,color:C.text}}>Activate {role.name}</div>
            <div style={{fontSize:11,color:C.muted}}>{role.is_free?'Free activation':role.price_txc+' TXC subscription'}</div>
          </div>
          <button onClick={onClose} style={{background:C.input,border:'none',borderRadius:10,width:34,height:34,cursor:'pointer',fontSize:16,color:C.text}}>✕</button>
        </div>

        {/* Scrollable form */}
        <div style={{overflowY:'auto',flex:1,padding:'16px'}}>

          {/* Balance check for paid roles */}
          {role.price_txc > 0 && (
            <div style={{
              background: balance.txc >= role.price_txc ? '#10b98118' : '#ef444418',
              border:`1px solid ${balance.txc >= role.price_txc ? '#10b98133' : '#ef444433'}`,
              borderRadius:14,padding:'12px 16px',marginBottom:16,
            }}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:C.text}}>Subscription Required</div>
                  <div style={{fontSize:11,color:C.muted}}>Monthly renewal · 30 days access</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:18,fontWeight:900,color:balance.txc>=role.price_txc?'#10b981':'#ef4444'}}>{role.price_txc} TXC</div>
                  <div style={{fontSize:10,color:C.muted}}>Your balance: {Math.round(balance.txc).toLocaleString()}</div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div style={{background:'#ef444418',border:'1px solid #ef444433',borderRadius:12,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#ef4444'}}>
              ⚠️ {error}
            </div>
          )}

          {loading ? (
            <div style={{textAlign:'center',padding:32}}>
              <div style={{width:32,height:32,border:`3px solid ${C.border}`,borderTop:`3px solid ${C.primary}`,borderRadius:'50%',margin:'0 auto',animation:'spin 0.8s linear infinite'}}/>
            </div>
          ) : (
            <div>
              {fields.map((field, i) => (
                <div key={i} style={{marginBottom:14}}>
                  <div style={{fontSize:12,color:C.muted,fontWeight:600,marginBottom:6}}>
                    {field.label} {field.required && <span style={{color:'#ef4444'}}>*</span>}
                  </div>
                  {field.type === 'select' ? (
                    <select
                      value={formData[field.key]||''}
                      onChange={e=>setFormData(prev=>({...prev,[field.key]:e.target.value}))}
                      style={{width:'100%',padding:'12px 14px',background:C.input,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:13,boxSizing:'border-box',outline:'none'}}
                    >
                      <option value="">Select {field.label}</option>
                      {(field.options||[]).map((opt,j)=>(
                        <option key={j} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type==='number'?'number':'text'}
                      placeholder={`Enter ${field.label}`}
                      value={formData[field.key]||''}
                      onChange={e=>setFormData(prev=>({...prev,[field.key]:e.target.value}))}
                      style={{width:'100%',padding:'12px 14px',background:C.input,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:13,boxSizing:'border-box',outline:'none'}}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Activate button */}
        <div style={{padding:'12px 16px 24px',borderTop:`1px solid ${C.border}`,flexShrink:0}}>
          <button onClick={handleActivate} disabled={activating||loading} style={{
            width:'100%',padding:'14px',
            background:activating?C.muted:role.color,
            border:'none',borderRadius:14,color:'#fff',
            fontSize:14,fontWeight:700,cursor:activating?'not-allowed':'pointer',
            boxShadow:`0 4px 20px ${role.color}44`,
          }}>
            {activating ? '⏳ Activating...' : role.is_free ? `✓ Activate ${role.name} (Free)` : `Pay ${role.price_txc} TXC & Activate`}
          </button>
        </div>
      </div>
    </div>
  );
}


function ChangePwdModal({ C, onClose }) {
  const [oldPwd, setOldPwd] = React.useState('');
  const [newPwd, setNewPwd] = React.useState('');
  const [confirmPwd, setConfirmPwd] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleChange = async () => {
    if (!oldPwd || !newPwd || !confirmPwd) { setError('All fields required'); return; }
    if (newPwd !== confirmPwd) { setError('Passwords do not match'); return; }
    if (newPwd.length < 8) { setError('Min 8 characters'); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch('/api/backend/v1/auth/change-password', {
        method:'POST',
        headers:{'Content-Type':'application/json','Authorization':`Bearer ${token}`},
        body:JSON.stringify({ old_password:oldPwd, new_password:newPwd }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error||'Failed');
      alert('Password changed successfully!');
      onClose();
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:10000,display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.bg,borderRadius:'24px 24px 0 0',width:'100%',maxWidth:430,padding:'24px 20px 40px'}}>
        <div style={{fontSize:18,fontWeight:800,color:C.text,marginBottom:20}}>🔐 Change Password</div>
        {error && <div style={{background:'#ef444418',border:'1px solid #ef444433',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#ef4444'}}>{error}</div>}
        {[
          {label:'Current Password', value:oldPwd, set:setOldPwd},
          {label:'New Password',     value:newPwd, set:setNewPwd},
          {label:'Confirm Password', value:confirmPwd, set:setConfirmPwd},
        ].map((f,i)=>(
          <div key={i} style={{marginBottom:12}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:5}}>{f.label}</div>
            <input type="password" value={f.value} onChange={e=>f.set(e.target.value)}
              style={{width:'100%',padding:'12px 14px',background:C.input,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:14,boxSizing:'border-box',outline:'none'}}
            />
          </div>
        ))}
        <button onClick={handleChange} disabled={loading} style={{
          width:'100%',background:C.primary,border:'none',borderRadius:12,
          padding:'14px',color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer',marginTop:8,
        }}>{loading?'Changing...':'Change Password'}</button>
      </div>
    </div>
  );

}

export default function ProfileScreen() {
  const C = useTheme();
  useStatusBar('#ffffff');
  const nav = useNavigate();
  const { user, balance, setBalance } = useStore();
  const [activeRoles, setActiveRoles] = useState(['consumer']);
  const [allRoles, setAllRoles] = useState([]);
  const [searchParams] = useSearchParams();
  const initialTab = ['overview','settings'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
  const [tab, setTab] = useState(initialTab);
  React.useEffect(() => {
    const t = searchParams.get('tab');
    if (t && ['overview','settings'].includes(t) && t !== tab) setTab(t);
  }, [searchParams]);
  useEffect(() => {
    if(!user?.id) return;
    wallet.balance(user.id)
      .then(b => { if(b?.txc_balance > 0) setBalance({ txc: b.txc_balance, vgc: balance.vgc||0 }); })
      .catch(()=>{});
  }, [user?.id]);

  const [isPartner, setIsPartner] = useState(false);
  const [reviewOrder, setReviewOrder] = useState(null);
  const [disputeOrder, setDisputeOrder] = useState(null);
  
  useEffect(() => {
    import('../services/api').then(({ partner }) => {
      partner.subscription().then(r => setIsPartner(r?.is_active || false)).catch(()=>{});
    });
  }, []);
  const [selectedRole, setSelectedRole] = useState(null);
  const [dashCache, setDashCache] = useState({});
  const [loadingRole, setLoadingRole] = useState(null);
  const [activating, setActivating] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('theme') === 'dark');
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showLanguage, setShowLanguage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ordersData, setOrdersData] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersFilter, setOrdersFilter] = useState('all');
  const [myProviderCats, setMyProviderCats] = useState([]);
  const [activationRole, setActivationRole] = useState(null);

  useEffect(() => {
    loadMyRoles();
    rolesConfig.list().then(r => setAllRoles(r.roles || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'myorders') {
      if (!ordersData) {
        setOrdersLoading(true);
        myOrdersApi.combined()
          .then(r => setOrdersData(r))
          .catch(() => setOrdersData({ my_requests:[], buyer_orders:[], seller_orders:[], summary:{} }))
          .finally(() => setOrdersLoading(false));
      }
      // Load provider categories for dynamic filters
      providerApi.myCategories()
        .then(r => setMyProviderCats(r.categories || []))
        .catch(() => setMyProviderCats([]));
    }
  }, [tab]);

  const loadMyRoles = async () => {
    try {
      const r = await rolesConfig.myRoles();
      const ids = (r.roles || []).map(x => x.role_id || x.id);
      setActiveRoles(ids.length ? ids : ['consumer']);
    } catch(e) {
      // fallback to consumer only
      setActiveRoles(['consumer']);
    }
  };

  const loadDashboard = useCallback(async (roleId) => {
    if (dashCache[roleId]) return;
    setLoadingRole(roleId);
    try {
      const r = await rolesApi.dashboard(roleId);
      setDashCache(prev => ({ ...prev, [roleId]: r }));
    } catch(e) {}
    setLoadingRole(null);
  }, [dashCache]);

  useEffect(() => {
    if (!selectedRole) return;
    const active = selectedRole.id === 'consumer' || activeRoles.includes(selectedRole.id);
    if (active && !dashCache[selectedRole.id]) {
      loadDashboard(selectedRole.id);
    }
  }, [selectedRole, activeRoles]);

  const activateRole = (role) => {
    setActivationRole(role);
  };

  const onRoleActivated = (role) => {
    toast.success(`🎉 ${role.name} activated!`);
    setActivationRole(null);
    setActiveRoles(prev => [...new Set([...prev, role.id])]);
    loadDashboard(role.id);
    if (role.price_txc > 0) setBalance({ ...balance, txc: balance.txc - role.price_txc });
  };

  const toggleTheme = () => {
    const t = darkMode ? 'light' : 'dark';
    localStorage.setItem('theme', t);
    setDarkMode(!darkMode);
    window.location.reload();
  };

  const isActive = (roleId) => roleId === 'consumer' || activeRoles.includes(roleId);
  const level = getLevel(balance.txc||0);
  const nextLevel = getNextLevel(balance.txc||0);
  const progress = nextLevel ? Math.min(100, ((balance.txc||0) / nextLevel.min) * 100) : 100;
  const name = user?.full_name || user?.username || user?.email?.split('@')[0] || 'User';

  return (
    <div style={{ background:'#ffffff', color:C.text }}>

      {/* HEADER */}
      <div style={{position:'sticky',top:0,zIndex:100,background:'#1a3cc8'}}>
        <div style={{padding:'16px 16px 12px'}}>
          <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:14}}>
            <div style={{width:54,height:54,borderRadius:'50%',flexShrink:0,
              background:'rgba(255,255,255,0.2)',
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:22,fontWeight:900,color:'#fff',
              border:'2px solid rgba(255,255,255,0.4)'}}>
              {name.charAt(0).toUpperCase()}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:17,fontWeight:800,color:'#fff',lineHeight:1.2}}>
                {name.charAt(0).toUpperCase()+name.slice(1)}
              </div>
              <div style={{fontSize:11,color:'rgba(255,255,255,0.65)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                {user?.email}
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
              <button onClick={()=>setShowQR(true)} style={{
                width:36,height:36,borderRadius:10,
                background:'rgba(255,255,255,0.15)',
                border:'1px solid rgba(255,255,255,0.3)',cursor:'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
              }}>
                <svg viewBox="0 0 24 24" width="18" height="18" stroke='#fff' strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="5" height="5"/><rect x="16" y="3" width="5" height="5"/><rect x="3" y="16" width="5" height="5"/>
                  <rect x="8" y="8" width="2" height="2" fill={C.text} stroke="none"/><rect x="11" y="3" width="2" height="2" fill={C.text} stroke="none"/>
                  <rect x="14" y="8" width="2" height="2" fill={C.text} stroke="none"/><rect x="19" y="10" width="2" height="2" fill={C.text} stroke="none"/>
                  <rect x="11" y="11" width="2" height="2" fill={C.text} stroke="none"/><rect x="14" y="14" width="2" height="2" fill={C.text} stroke="none"/>
                  <rect x="8" y="16" width="2" height="2" fill={C.text} stroke="none"/><rect x="11" y="19" width="2" height="2" fill={C.text} stroke="none"/>
                  <rect x="16" y="19" width="2" height="2" fill={C.text} stroke="none"/>
                </svg>
              </button>
              <div style={{background:level.color+'18',border:`1px solid ${level.color}33`,
                color:level.color,padding:'5px 12px',borderRadius:20,fontSize:11,fontWeight:700}}>
                {level.icon} {level.label}
              </div>
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <div onClick={()=>nav('/wallet')} style={{flex:1,
              background:'#1a3cc8',
              border:'none',borderRadius:12,padding:'10px 14px',
              display:'flex',alignItems:'center',justifyContent:'space-between',cursor:'pointer'}}>
              <div>
                <div style={{fontSize:10,color:'rgba(255,255,255,0.65)',fontWeight:600}}>TXC BALANCE</div>
                <div style={{fontSize:18,fontWeight:900,color:'#fff'}}>{Math.round(balance.txc||0).toLocaleString()} <span style={{fontSize:11,color:'rgba(255,255,255,0.65)'}}>TXC</span></div>
              </div>
              <svg viewBox="0 0 24 24" width="20" height="20" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="3"/><path d="M2 10h20"/><circle cx="17" cy="15" r="1.5" fill="#fff" stroke="none"/></svg>
            </div>
            <div style={{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',
              borderRadius:12,padding:'10px 14px',textAlign:'center',minWidth:80}}>
              <div style={{fontSize:10,color:'rgba(255,255,255,0.65)',fontWeight:600}}>SINCE</div>
              <div style={{fontSize:13,fontWeight:800,color:'#fff'}}>
                {user?.created_at?new Date(user.created_at).toLocaleDateString('en',{month:'short',year:'numeric'}):'New'}
              </div>
            </div>
          </div>
        </div>
      </div>


      <div style={{padding:'16px 16px 100px'}}>

        {/* ── SETTINGS ── */}
        {/* SETTINGS */}
        <div style={{paddingTop:8}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:1,marginBottom:10,paddingLeft:2}}>PREFERENCES</div>
          <div style={{background:C.card,border:'1px solid '+C.border,borderRadius:16,marginBottom:16,overflow:'hidden'}}>
            <LanguagePickerCard C={C} />
            <div style={{height:1,background:C.border,margin:'0 16px'}}/>
            <PushNotifCard C={C} />
            <div style={{height:1,background:C.border,margin:'0 16px'}}/>
            <div style={{padding:'13px 16px',display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:34,height:34,borderRadius:9,background:'#f59e0b18',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <svg viewBox="0 0 24 24" width="18" height="18" stroke="#f59e0b" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  {darkMode?<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>:<><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/></>}
                </svg>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{darkMode?'Dark Mode':'Light Mode'}</div>
              </div>
              <button onClick={toggleTheme} style={{width:46,height:26,borderRadius:20,background:darkMode?C.primary:'#e5e7eb',border:'none',cursor:'pointer',position:'relative',flexShrink:0}}>
                <div style={{width:20,height:20,borderRadius:'50%',background:'#fff',position:'absolute',top:3,left:darkMode?23:3,transition:'left 0.3s',boxShadow:'0 1px 4px rgba(0,0,0,0.2)'}}/>
              </button>
            </div>
          </div>

          <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:1,marginBottom:10,paddingLeft:2}}>ACCOUNT</div>
          <div style={{background:C.card,border:'1px solid '+C.border,borderRadius:16,marginBottom:16,overflow:'hidden'}}>
            {[
              {label:'KYC / Verification',sub:'Complete identity verification',color:'#10b981',action:()=>nav('/kyc'),
                svg:<svg viewBox="0 0 24 24" width="18" height="18" stroke="#10b981" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><line x1="7" y1="9" x2="17" y2="9"/><line x1="7" y1="13" x2="13" y2="13"/></svg>},
              {label:'Change Password',sub:'Update your password',color:'#1a3cc8',action:()=>setShowChangePwd(true),
                svg:<svg viewBox="0 0 24 24" width="18" height="18" stroke="#1a3cc8" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>},
              {label:'Set Location',sub:user?.city||localStorage.getItem('user_city')||'Tap to set your city',color:'#f97316',action:()=>setShowLocation(true),
                svg:<svg viewBox="0 0 24 24" width="18" height="18" stroke="#f97316" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>},
              {label:'My Disputes',sub:'View and respond to disputes',color:'#7c3aed',action:()=>nav('/disputes'),
                svg:<svg viewBox="0 0 24 24" width="18" height="18" stroke="#7c3aed" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>},
            ].map((item,i,arr)=>(
              <React.Fragment key={i}>
                <div onClick={item.action} style={{padding:'13px 16px',display:'flex',alignItems:'center',gap:12,cursor:'pointer'}}>
                  <div style={{width:34,height:34,borderRadius:9,background:item.color+'18',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{item.svg}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:C.text}}>{item.label}</div>
                    <div style={{fontSize:11,color:C.muted}}>{item.sub}</div>
                  </div>
                  <svg viewBox="0 0 24 24" width="16" height="16" stroke={C.muted} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </div>
                {i<arr.length-1&&<div style={{height:1,background:C.border,margin:'0 16px'}}/>}
              </React.Fragment>
            ))}
          </div>
              {user?.role === 'admin' && (
                <div onClick={()=>nav('/admin')} style={{
                  background:'linear-gradient(135deg, #7c3aed, #4f46e5)',
                  border:'none',borderRadius:16,padding:'16px',marginBottom:12,
                  cursor:'pointer',display:'flex',alignItems:'center',gap:12,
                  boxShadow:'0 4px 20px rgba(124, 58, 237, 0.3)',
                }}>
                  <div style={{fontSize:28}}>⚡</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:14,fontWeight:800,color:'#fff'}}>Admin Panel</div>
                    <div style={{fontSize:11,color:'rgba(255,255,255,0.8)'}}>VANTA Management</div>
                  </div>
                  <div style={{fontSize:20,color:'#fff'}}>→</div>
                </div>
              )}



            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,overflow:'hidden',marginBottom:12}}>
              {[
                {label:'Terms of Service', action:()=>nav('/terms'),
                  svg:<svg viewBox="0 0 24 24" width="18" height="18" stroke="#1a3cc8" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>},
                {label:'Privacy Policy', action:()=>nav('/privacy'),
                  svg:<svg viewBox="0 0 24 24" width="18" height="18" stroke="#7c3aed" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>},
                {label:'Contact Support', action:()=>window.location.href='mailto:support@vgo.global',
                  sub:'support@vgo.global',
                  svg:<svg viewBox="0 0 24 24" width="18" height="18" stroke="#10b981" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>},
              ].map((item,i,arr)=>(
                <React.Fragment key={i}>
                  <div onClick={item.action} style={{padding:'13px 16px',display:'flex',alignItems:'center',gap:12,cursor:'pointer'}}>
                    <div style={{width:34,height:34,borderRadius:9,background:'#f5f5f5',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{item.svg}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.text}}>{item.label}</div>
                      {item.sub&&<div style={{fontSize:11,color:C.muted}}>{item.sub}</div>}
                    </div>
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke={C.muted} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </div>
                  {i<arr.length-1&&<div style={{height:1,background:C.border,margin:'0 16px'}}/>}
                </React.Fragment>
              ))}
            </div>
            <div style={{background:C.card,border:'1px solid #ef444433',borderRadius:18,padding:18,boxShadow:C.shadow}}>
              <div style={{fontSize:13,fontWeight:700,color:'#ef4444',marginBottom:14}}>Account Actions</div>
              <button onClick={()=>{localStorage.clear();nav('/login');}} style={{width:'100%',background:'#ef444412',border:'1px solid #ef444433',borderRadius:12,padding:'13px',color:'#ef4444',fontSize:14,fontWeight:700,cursor:'pointer',marginBottom:10,display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="#ef4444" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Logout
              </button>
              <button onClick={()=>setShowDeleteConfirm(true)} style={{width:'100%',background:'transparent',border:'1px solid #ef444433',borderRadius:12,padding:'13px',color:'#ef4444',fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="#ef4444" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                Delete Account
              </button>
            </div>
          </div>

      </div>

      {/* MODALS */}
      {showChangePwd && <ChangePwdModal C={C} onClose={()=>setShowChangePwd(false)} />}
      {activationRole && <RoleActivationModal role={activationRole} C={C} balance={balance} onClose={()=>setActivationRole(null)} onActivated={onRoleActivated} />}

}

      {/* QR CODE MODAL */}
      {showQR && (
        <div onClick={()=>setShowQR(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',padding:24,backdropFilter:'blur(4px)'}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.bg,borderRadius:24,padding:28,width:'100%',maxWidth:320,textAlign:'center',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
            {/* Avatar */}
            <div style={{width:60,height:60,borderRadius:'50%',background:`linear-gradient(135deg,${C.primary},#7c3aed)`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,fontWeight:900,color:'#fff',margin:'0 auto 8px'}}>
              {name.charAt(0).toUpperCase()}
            </div>
            <div style={{fontSize:15,fontWeight:800,color:C.text,marginBottom:2}}>{name.charAt(0).toUpperCase()+name.slice(1)}</div>
            <div style={{fontSize:11,color:C.muted,marginBottom:20}}>VGO Member · {level.icon} {level.label}</div>
            {/* QR Code */}
            <div style={{background:'#fff',borderRadius:16,padding:16,display:'inline-block',marginBottom:16,boxShadow:'0 4px 20px rgba(0,0,0,0.1)'}}>
              <QRCodeSVG
                value={'https://nexus.vgo.global/join?ref='+(user?.referral_code||user?.username||'vgo')}
                size={180}
                fgColor="#1a3cc8"
                bgColor="#ffffff"
                level="M"
              />
            </div>
            {/* Referral link */}
            <div style={{background:C.input,border:`1px solid ${C.border}`,borderRadius:10,padding:'8px 12px',marginBottom:16,fontSize:11,color:C.muted,wordBreak:'break-all'}}>
              nexus.vgo.global/join?ref={user?.referral_code||user?.username}
            </div>
            <div style={{display:'flex',gap:8}}>
              <button onClick={()=>{navigator.clipboard?.writeText('https://nexus.vgo.global/join?ref='+(user?.referral_code||user?.username));toast.success('Link copied!');}} style={{flex:1,background:C.primary,border:'none',borderRadius:12,padding:'12px',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer'}}>
                Copy Link
              </button>
              <button onClick={()=>setShowQR(false)} style={{flex:1,background:C.input,border:`1px solid ${C.border}`,borderRadius:12,padding:'12px',color:C.text,fontSize:13,fontWeight:600,cursor:'pointer'}}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showLocation && (
        <div onClick={()=>setShowLocation(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:10000,display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.bg,borderRadius:'24px 24px 0 0',width:'100%',maxWidth:430,padding:'20px 20px 40px'}}>
            <div style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:16}}>📍 Set Your City</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              {['Mumbai','Delhi','Bangalore','Hyderabad','Chennai','Kolkata','Pune','Ahmedabad','Vijayawada','Visakhapatnam','Coimbatore','Jaipur','Lucknow','Surat','Indore','Kochi'].map((city,idx)=>(
                <div key={idx} onClick={()=>{localStorage.setItem('user_city',city);setShowLocation(false);}} style={{padding:'12px 14px',borderRadius:12,cursor:'pointer',textAlign:'center',background:localStorage.getItem('user_city')===city?C.primary+'18':C.input,border:`1px solid ${localStorage.getItem('user_city')===city?C.primary:C.border}`,fontSize:13,fontWeight:600,color:C.text}}>{city}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div onClick={()=>setShowDeleteConfirm(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 20px'}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.bg,borderRadius:20,padding:24,width:'100%',maxWidth:380}}>
            <div style={{width:56,height:56,borderRadius:'50%',background:'#ef444418',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px'}}>
              <svg viewBox="0 0 24 24" width="28" height="28" stroke="#ef4444" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div style={{fontSize:16,fontWeight:800,color:C.text,textAlign:'center',marginBottom:8}}>Delete Account?</div>
            <div style={{fontSize:13,color:C.muted,textAlign:'center',marginBottom:20,lineHeight:1.5}}>This will permanently delete your account and all data. This cannot be undone.</div>
            <button onClick={()=>{localStorage.clear();nav('/login');}} style={{width:'100%',background:'#ef4444',border:'none',borderRadius:12,padding:'14px',color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer',marginBottom:10}}>Yes, Delete My Account</button>
            <button onClick={()=>setShowDeleteConfirm(false)} style={{width:'100%',background:C.input,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px',color:C.text,fontSize:14,fontWeight:700,cursor:'pointer'}}>Cancel</button>
          </div>
        </div>
      )}

    </div>
  );
}

// ────────────────────────────────────────────────────────
// REVIEW MODAL
// ────────────────────────────────────────────────────────
function ReviewModal({ C, order, onClose, onSuccess }) {
  const [rating, setRating] = React.useState(0);
  const [review, setReview] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    if (rating < 1) return;
    setSubmitting(true);
    try {
      await reviewsApi.submit({ order_id: order.id, rating, review });
      onSuccess();
    } catch(e) {
      alert(e.message || 'Failed to submit review');
    }
    setSubmitting(false);
  };

  return (
    <div onClick={onClose} style={{
      position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',
      zIndex:99999,display:'flex',alignItems:'flex-end',justifyContent:'center',
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:C.card,borderRadius:'20px 20px 0 0',padding:'24px 20px',
        width:'100%',maxWidth:500,maxHeight:'90vh',overflow:'auto',
      }}>
        <div style={{textAlign:'center',marginBottom:16}}>
          <div style={{fontSize:18,fontWeight:800,color:C.text,marginBottom:4}}>⭐ Leave a Review</div>
          <div style={{fontSize:12,color:C.muted}}>{order.title}</div>
        </div>

        <div style={{display:'flex',justifyContent:'center',gap:8,marginBottom:16}}>
          {[1,2,3,4,5].map(n => (
            <button key={n} onClick={()=>setRating(n)} style={{
              background:'transparent',border:'none',
              fontSize:36,cursor:'pointer',padding:0,
              opacity:rating>=n?1:0.3,
              transform:rating>=n?'scale(1.1)':'scale(1)',
              transition:'all 0.15s',
            }}>⭐</button>
          ))}
        </div>
        
        <div style={{textAlign:'center',fontSize:12,color:C.muted,marginBottom:16}}>
          {rating===0 && 'Tap to rate'}
          {rating===1 && '😞 Poor'}
          {rating===2 && '😐 Fair'}
          {rating===3 && '🙂 Good'}
          {rating===4 && '😊 Very Good'}
          {rating===5 && '🤩 Excellent'}
        </div>

        <textarea
          value={review}
          onChange={e=>setReview(e.target.value)}
          placeholder="Share your experience (optional)"
          maxLength={1000}
          rows={4}
          style={{
            width:'100%',background:C.input,border:`1px solid ${C.border}`,
            borderRadius:12,padding:'10px 14px',color:C.text,fontSize:13,
            resize:'vertical',outline:'none',fontFamily:'inherit',boxSizing:'border-box',
            marginBottom:8,
          }}
        />
        <div style={{fontSize:10,color:C.muted,textAlign:'right',marginBottom:16}}>
          {review.length}/1000
        </div>

        <div style={{display:'flex',gap:8}}>
          <button onClick={onClose} style={{
            flex:1,background:C.input,border:`1px solid ${C.border}`,
            borderRadius:12,padding:'12px',color:C.muted,fontSize:13,fontWeight:600,cursor:'pointer',
          }}>Cancel</button>
          <button onClick={submit} disabled={submitting || rating<1} style={{
            flex:2,
            background:(submitting||rating<1)?C.muted:'#f59e0b',
            border:'none',borderRadius:12,padding:'12px',color:'#fff',
            fontSize:13,fontWeight:700,cursor:(submitting||rating<1)?'not-allowed':'pointer',
          }}>
            {submitting?'Submitting...':'Submit Review'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
// DISPUTE MODAL
// ────────────────────────────────────────────────────────
function DisputeModal({ C, order, onClose, onSuccess }) {
  const [reason, setReason] = React.useState('');
  const [desc, setDesc] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const REASONS = [
    { id: 'not_delivered', label: 'Service not delivered', icon: '🚫' },
    { id: 'wrong_item', label: 'Wrong item/service', icon: '❓' },
    { id: 'quality_issue', label: 'Quality issue', icon: '⚠️' },
    { id: 'payment_issue', label: 'Payment issue', icon: '💰' },
    { id: 'other', label: 'Other', icon: '📋' },
  ];

  const submit = async () => {
    if (!reason || !desc.trim()) return;
    setSubmitting(true);
    try {
      const r = await disputesApi.raise({ order_id: order.id, reason, description: desc.trim() });
      onSuccess(r.dispute_id);
    } catch(e) {
      alert(e.message || 'Failed');
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',
      zIndex:99999,display:'flex',alignItems:'flex-end',justifyContent:'center',
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:C.card,borderRadius:'20px 20px 0 0',padding:'24px 20px',
        width:'100%',maxWidth:500,maxHeight:'90vh',overflow:'auto',
      }}>
        <div style={{textAlign:'center',marginBottom:16}}>
          <div style={{fontSize:18,fontWeight:800,color:C.text,marginBottom:4}}>⚖️ Raise Dispute</div>
          <div style={{fontSize:12,color:C.muted}}>{order.title}</div>
        </div>

        <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:8}}>What went wrong?</div>
        <div style={{display:'grid',gap:6,marginBottom:16}}>
          {REASONS.map(r => (
            <button key={r.id} onClick={()=>setReason(r.id)} style={{
              background: reason===r.id ? '#ef444418' : C.input,
              border: `1px solid ${reason===r.id ? '#ef4444' : C.border}`,
              borderRadius:10,padding:'10px 12px',
              color: reason===r.id ? '#ef4444' : C.text,
              fontSize:12,fontWeight: reason===r.id ? 700 : 500,
              cursor:'pointer',textAlign:'left',
              display:'flex',alignItems:'center',gap:8,
            }}>
              <span style={{fontSize:16}}>{r.icon}</span>
              <span>{r.label}</span>
            </button>
          ))}
        </div>

        <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:6}}>Describe the issue</div>
        <textarea value={desc} onChange={e=>setDesc(e.target.value)}
          placeholder="Provide as much detail as possible..." maxLength={2000} rows={4}
          style={{width:'100%',background:C.input,border:`1px solid ${C.border}`,borderRadius:12,padding:'10px 14px',color:C.text,fontSize:13,resize:'vertical',outline:'none',fontFamily:'inherit',boxSizing:'border-box',marginBottom:16}}/>

        <div style={{background:'#f9731618',border:'1px solid #f9731633',borderRadius:10,padding:'10px',marginBottom:16,fontSize:11,color:C.text,lineHeight:1.4}}>
          ⚠️ An admin will review your dispute within 24 hours. Possible outcomes: full refund, partial refund, or dispute rejected.
        </div>

        <div style={{display:'flex',gap:8}}>
          <button onClick={onClose} style={{flex:1,background:C.input,border:`1px solid ${C.border}`,borderRadius:12,padding:'12px',color:C.muted,fontSize:13,fontWeight:600,cursor:'pointer'}}>Cancel</button>
          <button onClick={submit} disabled={submitting||!reason||!desc.trim()} style={{
            flex:2,
            background:(submitting||!reason||!desc.trim())?C.muted:'#ef4444',
            border:'none',borderRadius:12,padding:'12px',color:'#fff',
            fontSize:13,fontWeight:700,cursor:(submitting||!reason||!desc.trim())?'not-allowed':'pointer',
          }}>
            {submitting?'Submitting...':'Submit Dispute'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
// LANGUAGE PICKER CARD
// ────────────────────────────────────────────────────────
function LanguagePickerCard({ C }) {
  const { lang, setLang } = useLang();
  const [expanded, setExpanded] = React.useState(false);
  const current = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];
  return (
    <div>
      <div onClick={()=>setExpanded(!expanded)} style={{
        padding:'13px 16px',display:'flex',alignItems:'center',gap:12,cursor:'pointer',
      }}>
        <div style={{width:34,height:34,borderRadius:9,background:'#1a3cc818',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
          <svg viewBox="0 0 24 24" width="18" height="18" stroke="#1a3cc8" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:600,color:C.text}}>Language</div>
          <div style={{fontSize:11,color:C.muted}}>{current.flag} {current.name}</div>
        </div>
        <svg viewBox="0 0 24 24" width="16" height="16" stroke={C.muted} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{transform:expanded?'rotate(90deg)':'none',transition:'transform 0.2s'}}>
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </div>
      {expanded && (
        <div style={{padding:'0 16px 14px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
          {LANGUAGES.map(l => (
            <button key={l.code} onClick={()=>{setLang(l.code);setExpanded(false);}} style={{
              background:lang===l.code?C.primary:C.input,
              color:lang===l.code?'#fff':C.text,
              border:`1px solid ${lang===l.code?C.primary:C.border}`,
              borderRadius:10,padding:'9px 10px',fontSize:12,
              fontWeight:lang===l.code?700:500,cursor:'pointer',
              textAlign:'left',display:'flex',alignItems:'center',gap:6,
            }}>
              <span style={{fontSize:16}}>{l.flag}</span>
              <span>{l.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
function PushNotifCard({ C }) {
  const [permission, setPermission] = React.useState('default');
  const [enabling, setEnabling] = React.useState(false);
  
  React.useEffect(() => {
    getPushPermission().then(setPermission);
  }, []);
  
  const enable = async () => {
    setEnabling(true);
    const ok = await subscribePush();
    if (ok) setPermission('granted');
    setEnabling(false);
  };
  
  const disable = async () => {
    await unsubscribePush();
    setPermission('default');
  };
  
  if (permission === 'unsupported') return null;
  
  const isOn = permission === 'granted';
  
  return (
    <div style={{
      background: isOn ? `linear-gradient(135deg, #10b98118, ${C.primary}18)` : C.card,
      border: `1px solid ${isOn ? '#10b98133' : C.border}`,
      borderRadius:14, padding:'14px', marginBottom:10,
      display:'flex', alignItems:'center', gap:12,
    }}>
      <div style={{width:34,height:34,borderRadius:9,background:isOn?'#10b98118':'#64748b18',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
        <svg viewBox="0 0 24 24" width="18" height="18" stroke={isOn?'#10b981':'#64748b'} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
          {isOn ? <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></> : <><path d="M13.73 21a2 2 0 0 1-3.46 0"/><path d="M18.63 13A17.89 17.89 0 0 1 18 8"/><path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14"/><path d="M18 8a6 6 0 0 0-9.33-5"/><line x1="1" y1="1" x2="23" y2="23"/></>}
        </svg>
      </div>
      <div style={{flex:1}}>
        <div style={{fontSize:13,fontWeight:700,color:C.text}}>Push Notifications</div>
        <div style={{fontSize:10,color:C.muted}}>{isOn ? 'Enabled · You will get instant alerts' : 'Get alerts for orders, training, investments'}</div>
      </div>
      {isOn ? (
        <div style={{display:'flex',gap:6}}>
          <button onClick={testPush} style={{background:C.input,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 10px',fontSize:10,color:C.text,cursor:'pointer',fontWeight:600}}>Test</button>
          <button onClick={disable} style={{background:'#ef444418',border:'1px solid #ef444433',borderRadius:8,padding:'6px 10px',fontSize:10,color:'#ef4444',cursor:'pointer',fontWeight:600}}>Disable</button>
        </div>
      ) : (
        <button onClick={enable} disabled={enabling} style={{background:C.primary,border:'none',borderRadius:8,padding:'6px 12px',fontSize:11,color:'#fff',cursor:enabling?'wait':'pointer',fontWeight:700}}>
          {enabling ? '...' : 'Enable'}
        </button>
      )}
    </div>
  );
}

